import { formatDate } from "utils/helpers";
import { apiSlice } from "features/api/apiSlice";
import { createEntityAdapter } from "@reduxjs/toolkit";
import { RESULTS_PER_PAGE } from "constants";

const extensionAdapter = createEntityAdapter({});

const initialState = extensionAdapter.getInitialState({ size: 0 });

export const extensionApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getExtensions: builder.query({
      query: (searchParams) => `/extensions?${searchParams}&limit=${RESULTS_PER_PAGE}`,
      transformResponse: (response) => {
        if (!response?.results)
          return extensionAdapter.setAll(initialState, []);

        const fetchedExtensions = response.results.map((extension) => {
          const {
            alias,
            account,
            account_caller_id,
            created_by,
            profile_id,
            domain_id,
            caller_id,
            // did_id,
            // did,
            domain,
            account_id,
            display_name,
            username,
            local_call,
            enabled,
            call_billing_account_id,
            call_billing_account,
            created_by_id,
            variables,
            created_at,
            updated_at,
            parameters,
            user_id,
            user,
            ...extensionProps
          } = extension;

          extensionProps["local_call"] = local_call ? "Yes" : "No";
          extensionProps["enabled"] = enabled ? "Yes" : "No";

          // extensionProps[
          //   "created_by"
          // ] = `${created_by.lastname} ${created_by.firstname}`;

          extensionProps[
            "user"
          ] = `${user?.lastname} ${user?.firstname}`;

          return {
            username,
            domain: domain.name,
            account: account.number,
            // did: did.number,
            caller_id_number: account_caller_id.callerid_number,
            ...extensionProps,
            // created_at: formatDate(created_at),
            // updated_at: formatDate(updated_at),
          };
        });

        return extensionAdapter.setAll(
          { ...initialState, size: response?.total },
          fetchedExtensions
        );
      },
      providesTags: (result) =>
        result
          ? [
            ...result.ids.map((id) => ({ type: "Extension", id })),
            { type: "Extension", id: "PARTIAL-LIST" },
          ]
          : [{ type: "Extension", id: "PARTIAL-LIST" }],
    }),
    getExtension: builder.query({
      query: (id) => `/extensions/${id}`,
      providesTags: (result, error, id) => [{ type: "Extension", id }],
    }),
    getExtensionsByAccount: builder.query({
      query: ({ accountId, searchParams }) =>
        `/accounts/${accountId}/extensions?${searchParams}&limit=${RESULTS_PER_PAGE}`,
      transformResponse: (response) => {
        if (!response?.results)
          return extensionAdapter.setAll(initialState, []);

        const fetchedExtensions = response.results.map((extension) => {
          const {
            account,
            account_caller_id,
            created_by,
            profile_id,
            domain_id,
            caller_id,
            // did_id,
            // did,
            domain,
            account_id,
            display_name,
            local_call,
            enabled,
            call_billing_account_id,
            call_billing_account,
            created_by_id,
            variables,
            created_at,
            updated_at,
            parameters,
            ...extensionProps
          } = extension;

          extensionProps["local_call"] = local_call ? "Yes" : "No";
          extensionProps["enabled"] = enabled ? "Yes" : "No";

          extensionProps[
            "created_by"
          ] = `${created_by.lastname} ${created_by.firstname}`;

          return {
            account: account.number,
            domain: domain.name,
            // did: did.number,
            caller_id: account_caller_id.callerid_number,
            ...extensionProps,
            created_at: formatDate(created_at),
            // updated_at: formatDate(updated_at),
          };
        });

        return extensionAdapter.setAll(
          { ...initialState, size: response?.total },
          fetchedExtensions
        );
      },
      providesTags: (result, error, id) => [
        ...result.ids.map((id) => ({ type: "Extension", id })),
      ],
    }),
    getExtensionsByCall: builder.query({
      query: ({ accountId, searchParams }) =>
        `/accounts/${accountId}/extensions?${searchParams}&limit=${RESULTS_PER_PAGE}`,
      transformResponse: (response) => {
        if (!response?.results)
          return extensionAdapter.setAll(initialState, []);

        const fetchedExtensions = response.results.map((extension) => {
          const {
            account,
            account_caller_id,
            created_by,
            profile_id,
            domain_id,
            caller_id,
            // did_id,
            // did,
            domain,
            account_id,
            display_name,
            local_call,
            enabled,
            call_billing_account_id,
            call_billing_account,
            created_by_id,
            variables,
            created_at,
            updated_at,
            parameters,
            ...extensionProps
          } = extension;

          extensionProps["local_call"] = local_call ? "Yes" : "No";
          extensionProps["enabled"] = enabled ? "Yes" : "No";

          extensionProps[
            "created_by"
          ] = `${created_by.lastname} ${created_by.firstname}`;

          return {
            account: account.number,
            domain: domain.name,
            // did: did.number,
            caller_id: account_caller_id.callerid_number,
            ...extensionProps,
            created_at: formatDate(created_at),
            // updated_at: formatDate(updated_at),
          };
        });

        return extensionAdapter.setAll(
          { ...initialState, size: response?.total },
          fetchedExtensions
        );
      },
      providesTags: (result, error, id) => [
        ...result.ids.map((id) => ({ type: "Extension", id })),
      ],
    }),
    getExtensionsByUser: builder.query({
      query: ({ userId, searchParams }) =>
        `/users/${userId}/extensions?${searchParams}&limit=${RESULTS_PER_PAGE}`,
      transformResponse: (response) => {
        if (!response?.results || response.results.length === 0) {
          return extensionAdapter.setAll({ ...initialState, size: response?.total || 0 }, []);
        }

        const fetchedExtensions = response.results.map((extension) => {
          const {
            id, // Ensure the id field is used if available
            extension_id, // Use this if `id` is missing in the API response
            profile_id,
            domain_id,
            caller_id,
            did_id,
            did,
            domain,
            account_id,
            display_name,
            local_call,
            enabled,
            call_billing_account_id,
            call_billing_account,
            created_by_id,
            variables,
            created_at,
            updated_at,
            parameters,
            ...extensionProps
          } = extension;

          extensionProps["local_call"] = local_call ? "Yes" : "No";
          extensionProps["enabled"] = enabled ? "Yes" : "No";

          return {
            id: id || extension_id, // Use `id` or fallback to `extension_id` for uniqueness
            ...extensionProps,
            created_at: formatDate(created_at),
          };
        });

        return extensionAdapter.setAll(
          { ...initialState, size: response?.total },
          fetchedExtensions
        );
      },
      providesTags: (result) =>
        result?.ids
          ? [
            ...result.ids.map((id) => ({ type: "Extension", id })),
            { type: "Extension", id: "PARTIAL-LIST" },
          ]
          : [{ type: "Extension", id: "PARTIAL-LIST" }],
    }),

    createExtension: builder.mutation({
      query: (data) => ({
        url: "/extensions",
        method: "POST",
        body: JSON.stringify({
          ...data,
        }),
      }),
      invalidateTags: [{ type: "Extension", id: "PARTIAL-LIST" }],
    }),
    updateExtension: builder.mutation({
      query: ({ extensionId, ...data }) => ({
        url: `/extensions/${extensionId}`,
        method: "PATCH",
        body: JSON.stringify({
          id: extensionId,
          ...data,
        }),
      }),
      invalidateTags: (result, error, args) => [
        { type: "Extension", id: args.id },
      ],
    }),
    deleteExtension: builder.mutation({
      query: (id) => ({
        url: `/extensions/${id}`,
        method: "DELETE",
      }),
      invalidateTags: (result, error, args) => [
        { type: "Extension", id: args.id },
        { type: "Extension", id: "PARTIAL-LIST" },
      ],
    }),
    assignDID: builder.mutation({
      query: ({ extensionId, data }) => ({
        url: `/extensions/${extensionId}/dids`,
        method: "POST",
        body: JSON.stringify({
          ...data,
        }),
      }),
      invalidateTags: (result, error, args) => [
        { type: "Extension", id: args.id },
        { type: "Extension", id: "PARTIAL-LIST" },
      ],
    }),
  }),
});

export const {
  useGetExtensionQuery,
  useGetExtensionsQuery,
  useUpdateExtensionMutation,
  useCreateExtensionMutation,
  useDeleteExtensionMutation,
  useGetExtensionsByAccountQuery,
  useGetExtensionsByUserQuery,
  useAssignDIDMutation
} = extensionApiSlice;
